import createCustomHistory, { type CustomHistoryState } from '../history/customHistory';
import { URLUtils } from './resolveUrl';
import { SingleUrl } from './url-types';
import { service } from '@whiz-cart/node-shared/service/service';
import { createStore } from 'cross-state';
import type { Location, LocationDescriptorObject } from 'history';
import _, { isEmpty, pickBy } from 'lodash';
import { parse as qsParse, stringify as qsStringify } from 'qs';
import { useEffect } from 'react';

export type PushUrlArgs =
    | [pathname: string, search?: Record<string, any>, hash?: Record<string, any>, options?: PushUrlOptions]
    | [search: Record<string, any>, hash?: Record<string, any>, options?: PushUrlOptions];

export interface PushUrlOptions {
    state?: CustomHistoryState;
    returnLocation?: boolean | Omit<Location, 'state'>;
    replaceSearch?: boolean;
    replaceHash?: boolean;
}

export interface LocationTargetObject extends PushUrlOptions {
    pathname?: string;
    search?: Record<string, any>;
    hash?: Record<string, any>;
}

export type LocationTarget = string | LocationTargetObject;

export const urlService = service(
    'urlService',
    class UrlService {
        /** The browser history object. */
        history = createCustomHistory({ getUserConfirmation: (...args) => this.getUserConfirmation(...args) });
        hashBase = createStore<string | undefined>(undefined);

        parse = (s: string) =>
            _.mapValues(qsParse(s, { strictNullHandling: true }), (v) => (v === null ? true : v)) as { [key: string]: any };
        stringify = (o: { [key: string]: any }) =>
            qsStringify(
                _.mapValues(o, (v) => (v === true ? null : v === false ? undefined : v)),
                {
                    strictNullHandling: true,
                    arrayFormat: 'repeat',
                },
            );

        getUserConfirmation = (message: string, callback: (result: boolean) => void) => callback(window.confirm(message));

        state = createStore<SingleUrl>(({ connect }) => {
            connect(({ set }) => {
                return this.history.listen(() => set(this.getUrl()));
            });

            return this.getUrl();
        });

        getUrlParams() {
            return this.parse(this.history.location.search.substring(1));
        }

        getUrlHashParams() {
            return this.parse(this.history.location.hash.substring(1));
        }

        /** Return the current url. */
        getUrl() {
            return {
                path: this.history.location.pathname,
                params: this.getUrlParams(),
                hash: this.getUrlHashParams(),
                raw: {
                    search: this.history.location.search,
                    hash: this.history.location.hash,
                },
            } as SingleUrl;
        }

        push(target: LocationTarget) {
            const location = this.targetToLocation(target);
            this.history.push(location);
        }

        /** Sets a new url. */
        pushUrl(...args: PushUrlArgs) {
            const location = this.urlToLocation(...args);
            this.history.push(location);
        }

        replace(target: LocationTarget) {
            const location = this.targetToLocation(target);
            this.history.replace(location);
        }

        /** Replaces the current url. */
        replaceUrl(...args: PushUrlArgs) {
            const location = this.urlToLocation(...args);
            this.history.replace(location);
        }

        resetUrl() {
            this.history.push('/');
        }

        useHashBase(hashBase: string) {
            useEffect(() => {
                this.hashBase.set(hashBase);

                return () => {
                    this.hashBase.set(undefined);
                };
            }, [hashBase]);
        }

        isInCurrentHasBase(path: string) {
            path = path.replace(/\/+$/, '');
            const hashBase = this.hashBase.get()?.replace(/\/+$/, '') ?? '/' + (this.history.location.pathname.split('/')[1] ?? '');

            return path === hashBase || path.startsWith(hashBase + '/');
        }

        resolve(base: string, ...path: string[]) {
            return URLUtils.resolve(base, ...path);
        }

        /** Calculte a new url from the current one, overriding the given path and/or params. */
        calcUrl(...args: PushUrlArgs) {
            const location = this.urlToLocation(...args);
            return location.pathname + (location.search ?? '') + (location.hash ?? '');
        }

        targetToLocation(target: LocationTarget): LocationDescriptorObject<CustomHistoryState> {
            if (typeof target === 'string') {
                target = {
                    pathname: target,
                };
            }

            let basePath = window.location.origin + window.location.pathname;
            if (!basePath.endsWith('/')) {
                basePath += '/';
            }

            const pathname = new URL(target.pathname ?? '', basePath).pathname;

            const search = pickBy(
                {
                    ...(!target.replaceSearch ? this.getUrlParams() : undefined),
                    ...target.search,
                },
                (v) => v !== undefined,
            );

            const sameHashBase = this.isInCurrentHasBase(pathname);
            const hash = pickBy(
                {
                    ...(sameHashBase && !target.replaceHash ? this.getUrlHashParams() : undefined),
                    ...target.hash,
                },
                (v) => v !== undefined,
            );

            const state = pickBy(
                {
                    ...target.state,
                    returnLocation: target.returnLocation ?? target.state?.returnLocation,
                },
                (v) => v !== undefined,
            );

            return {
                pathname,
                search: isEmpty(search) ? undefined : '?' + this.stringify(search),
                hash: isEmpty(hash) ? undefined : '#' + this.stringify(hash),
                state,
            };
        }

        locationToTarget(location: Partial<Location<CustomHistoryState>>): LocationTargetObject {
            return {
                pathname: location.pathname,
                search: location.search ? this.parse(location.search.substring(1)) : undefined,
                hash: location.hash ? this.parse(location.hash.substring(1)) : undefined,
                state: location.state,
            };
        }

        urlToLocation(...args: PushUrlArgs) {
            let pathname = '';
            let search: Record<string, any> | undefined;
            let hash: Record<string, any> | undefined;
            let options: PushUrlOptions | undefined;

            if (typeof args[0] === 'string') {
                [pathname, search, hash, options] = args as [string, Record<string, any>?, Record<string, any>?, PushUrlOptions?];
            } else {
                [search, hash, options] = args as [Record<string, any>, Record<string, any>?, PushUrlOptions?];
            }

            return this.targetToLocation({ pathname, search, hash, ...options });
        }
    },
);
